import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
    DocumentController,
    FeedItemEntity,
    FeedItemTypeEnum,
    OrderController,
} from '@domain';
import { RaInputComponent, RaLoadingComponent } from '@shared/components';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RaBadgeComponent, RaIconComponent } from 'ra-ui';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { FeedMainService } from '../../services/feed-main.service';

/**
 * Painel da LIXEIRA (feature `unified_feed`): serviços e documentos removidos
 * (soft delete), com "expira em X dias" e restauração. Restaurar um serviço
 * traz os documentos dele junto; documento restaura individualmente.
 */
@Component({
    selector: 'order-trash-panel',
    templateUrl: 'order-trash-panel.component.html',
    styleUrl: 'order-trash-panel.component.scss',
    imports: [
        RaModalComponent,
        RaBadgeComponent,
        RaEmptyComponent,
        RaInputComponent,
        RaLoadingComponent,
        RaIconComponent,
        RaSquareIconComponent,
        ReactiveFormsModule,
        TranslatePipe,
    ],
})
export class OrderTrashPanelComponent implements OnInit {
    public close = output<void>();

    public loading = signal(true);
    public items = signal<FeedItemEntity[]>([]);
    public restoringId = signal<string | null>(null);

    /** Busca por nome (filtro local — a lixeira já vem inteira do backend). */
    public searchControl = new FormControl('');
    private _searchText = signal('');

    public filteredItems = computed(() => {
        const search = this._searchText().trim().toLowerCase();
        if (!search) return this.items();

        return this.items().filter(item => {
            const title = this.itemTitle(item).toLowerCase();
            const client = item.client?.name?.toLowerCase() ?? '';
            const description = item.description?.trim().toLowerCase() ?? '';

            return title.includes(search) || client.includes(search) || description.includes(search);
        });
    });

    public feedItemTypeEnum = FeedItemTypeEnum;

    // * [Injects]
    private _orderController = inject(OrderController);
    private _documentController = inject(DocumentController);
    private _feedMainService = inject(FeedMainService);

    constructor() {
        this.searchControl.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(value => this._searchText.set(value ?? ''));
    }

    ngOnInit(): void {
        this._getTrash();
    }

    /** Faltando pouco para o expurgo (≤ 5 dias) o prazo vira alerta (danger). */
    public isExpiringSoon(item: FeedItemEntity): boolean {
        return this.expiresInDays(item) <= 5;
    }

    private _getTrash(): void {
        this.loading.set(true);
        this._orderController
            .trash()
            .pipe(take(1))
            .subscribe({
                next: items => {
                    this.items.set(items ?? []);
                    this.loading.set(false);
                },
                error: () => this.loading.set(false),
            });
    }

    /** Título da linha: grupo = cliente (fallback descrição); doc = título. */
    public itemTitle(item: FeedItemEntity): string {
        if (item.type === FeedItemTypeEnum.GROUP) {
            return item.client?.name || item.description?.trim() || '-';
        }

        return item.document?.title || '-';
    }

    /** Dias restantes até o expurgo definitivo pelo backend. */
    public expiresInDays(item: FeedItemEntity): number {
        if (!item.dateToDelete) return 0;
        const diff = new Date(item.dateToDelete).getTime() - Date.now();

        return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
    }

    /**
     * Restaura o item: serviço via order/restore-deleted (docs voltam junto);
     * documento via document/restore.
     */
    public restore(item: FeedItemEntity): void {
        this.restoringId.set(item._id);

        const request$ =
            item.type === FeedItemTypeEnum.GROUP
                ? this._orderController.restoreDeleted(item._id)
                : this._documentController.restore(item.document?._id ?? item._id);

        request$.pipe(take(1)).subscribe({
            next: () => {
                this.restoringId.set(null);
                this.items.update(items => items.filter(i => i._id !== item._id));

                // Item restaurado volta ao feed ativo
                this._feedMainService.refreshSilent();
            },
            error: () => this.restoringId.set(null),
        });
    }
}
